import Layout from "@/components/Layout";
import { useParams, useNavigate, Navigate, Link } from "react-router-dom";
import { ArrowLeft, ArrowRight, CheckCircle, ListNumbers, Lightbulb, Compass } from "@phosphor-icons/react";
import { TOOLS, PILLARS } from "@/lib/tools";
import { TOOL_GUIDES } from "@/data/toolGuides";

export default function ToolGuide() {
  const { type } = useParams();
  const navigate = useNavigate();
  const tool = TOOLS[type];
  const guide = TOOL_GUIDES[type];

  if (!tool || !guide) return <Navigate to="/guide" replace />;
  const pillar = PILLARS[tool.pillar] || PILLARS.STRATEGY;

  return (
    <Layout>
      <div className="max-w-3xl mx-auto w-full py-10 px-6 lg:px-10">
        <Link to="/guide" data-testid="back-to-guide" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 transition-colors mb-5">
          <ArrowLeft size={16} /> Method Library
        </Link>

        <div className="flex items-center gap-2 mb-2">
          <span className={`w-2.5 h-2.5 rounded-full ${pillar.dot}`} />
          <span className={`font-mono text-xs font-bold uppercase tracking-[0.12em] ${pillar.text}`}>{pillar.label}</span>
        </div>
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900" data-testid="guide-title">{tool.name}</h1>
        <p className="mt-2 text-lg text-slate-600">{tool.tagline}</p>

        <section className="mt-8">
          <div className="flex items-center gap-2 mb-2">
            <Compass size={20} weight="duotone" className={pillar.text} />
            <h2 className="text-xl font-semibold text-slate-900">What it is</h2>
          </div>
          <p className="text-sm text-slate-700 leading-relaxed">{guide.what}</p>
        </section>

        <section className="mt-8">
          <div className="flex items-center gap-2 mb-3">
            <CheckCircle size={20} weight="duotone" className={pillar.text} />
            <h2 className="text-xl font-semibold text-slate-900">When to use it</h2>
          </div>
          <ul className="space-y-2">
            {guide.when.map((w, i) => (
              <li key={i} className="flex gap-2.5 text-sm text-slate-700 leading-relaxed">
                <span className={`mt-2 w-1.5 h-1.5 rounded-sm shrink-0 ${pillar.dot}`} />
                {w}
              </li>
            ))}
          </ul>
        </section>

        <section className="mt-8">
          <div className="flex items-center gap-2 mb-3">
            <ListNumbers size={20} weight="duotone" className={pillar.text} />
            <h2 className="text-xl font-semibold text-slate-900">How it works</h2>
          </div>
          <ol className="space-y-2.5">
            {guide.how.map((step, i) => (
              <li key={i} className="flex gap-3 bg-white border border-slate-200 rounded-sm p-4">
                <span className="w-7 h-7 shrink-0 rounded-sm bg-slate-900 text-white font-mono text-sm flex items-center justify-center">{i + 1}</span>
                <p className="text-sm text-slate-700 leading-relaxed pt-0.5">{step}</p>
              </li>
            ))}
          </ol>
        </section>

        <section className="mt-8">
          <div className={`rounded-sm p-5 border ${pillar.chip}`}>
            <div className="flex items-center gap-2 mb-2">
              <Lightbulb size={18} weight="duotone" />
              <h3 className="text-sm font-bold uppercase tracking-wide">Worked example</h3>
            </div>
            <p className="text-sm leading-relaxed">{guide.example}</p>
          </div>
        </section>

        <section className="mt-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 border-t border-slate-200 pt-8">
          <div>
            <h3 className="text-lg font-semibold text-slate-900">Ready to use {tool.name}?</h3>
            <p className="text-sm text-slate-500">Open the workspace and start a structured document.</p>
          </div>
          <button
            data-testid="guide-open-workspace"
            onClick={() => navigate("/dashboard")}
            className="inline-flex items-center gap-2 rounded-sm bg-slate-900 px-5 py-2.5 text-sm font-medium text-white hover:bg-slate-800 transition-colors"
          >
            Go to Workspace <ArrowRight size={16} />
          </button>
        </section>
      </div>
    </Layout>
  );
}
